import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import {
  commitExtraction,
  extractJobStatus,
  indexDocument,
  listExtractJobs,
  listGraphs,
  startExtraction,
  type ApiFailure,
  type ApiResult,
  type DocumentIndexResponse,
  type ExtractCommitResponse,
  type ExtractJobStatus,
  type GraphInfo,
  type LoadMode,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import { moduleEntitled, useEntitledModules } from '@/lib/usePages';
import { Message, messageClass } from '@/components/ui/Message';
import { describeApiError } from '@/lib/apiErrors';

const DATA_GRAPH = 'urn:tres:data';
const POLL_MS = 2000;

/**
 * Document extraction, ad hoc: one document in, a proposal set out, accepted
 * entity by entity, written through the load path. The server owns the run
 * (a job that outlives this tab); this panel starts it, polls it, and commits.
 */
export function ExtractPanel({ uploadsLocked = false }: { uploadsLocked?: boolean }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? '';
  const queryClient = useQueryClient();
  const entitled = useEntitledModules();
  const inPlan = moduleEntitled(entitled.data, 'doc-extract');

  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'start' | 'commit' | 'index' | null>(null);
  const [indexed, setIndexed] = useState<DocumentIndexResponse | null>(null);
  const [job, setJob] = useState<ExtractJobStatus | null>(null);
  const [recent, setRecent] = useState<ExtractJobStatus[]>([]);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [graphs, setGraphs] = useState<GraphInfo[]>([]);
  const [targetGraph, setTargetGraph] = useState(DATA_GRAPH);
  const [mode, setMode] = useState<LoadMode>('append');
  const [result, setResult] = useState<ApiResult<ExtractCommitResponse> | null>(null);

  function showError(r: ApiFailure) {
    setError(r.message);
  }

  useEffect(() => {
    if (!token) return;
    void listExtractJobs(token).then((r) => {
      if (r.ok) setRecent(r.data.jobs);
    });
  }, [token]);

  useEffect(() => {
    if (!token || !job || job.state !== 'running') return;
    const handle = window.setInterval(() => {
      void extractJobStatus(job.job_id, token).then((r) => {
        if (!r.ok) {
          showError(r);
          return;
        }
        setJob(r.data);
        if (r.data.state === 'done' && r.data.result) {
          setAccepted(new Set(r.data.result.entities.map((e) => e.id)));
        }
        if (r.data.state === 'failed') {
          setError(`The extraction did not finish. ${describeApiError(r.data.http_status ?? 500, r.data.error, r.data.detail)}`);
        }
      });
    }, POLL_MS);
    return () => window.clearInterval(handle);
  }, [job, token]);

  async function index() {
    if (!token || !file) return;
    setBusy('index');
    setError(null);
    setIndexed(null);
    const r = await indexDocument(file, token);
    setBusy(null);
    if (!r.ok) {
      showError(r);
      return;
    }
    setIndexed(r.data);
    invalidateAfterDataWrite(queryClient);
  }

  async function start() {
    if (!token || !file) return;
    setBusy('start');
    setError(null);
    setResult(null);
    setIndexed(null);
    setJob(null);
    const r = await startExtraction(file, token);
    setBusy(null);
    if (!r.ok) {
      showError(r);
      return;
    }
    const status = await extractJobStatus(r.data.job_id, token);
    if (status.ok) setJob(status.data);
    void listGraphs(token).then((g) => {
      if (g.ok) setGraphs(g.data.graphs);
    });
  }

  function openJob(j: ExtractJobStatus) {
    setError(null);
    setResult(null);
    setJob(j);
    if (j.result) setAccepted(new Set(j.result.entities.map((e) => e.id)));
    void listGraphs(token).then((g) => {
      if (g.ok) setGraphs(g.data.graphs);
    });
  }

  async function commit() {
    if (!token || !job?.result) return;
    setBusy('commit');
    setError(null);
    const r = await commitExtraction(
      job.job_id,
      { accepted_ids: Array.from(accepted), graph: targetGraph, mode },
      token,
    );
    setBusy(null);
    setResult(r);
    if (r.ok) {
      invalidateAfterDataWrite(queryClient);
    } else {
      showError(r);
    }
  }

  const btnPrimary = cn(
    'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground',
    'disabled:opacity-50',
  );
  const btnGhost = 'rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50';
  const selectCls = 'rounded-md border border-input bg-background px-2 py-1 text-sm';

  const outcome = job?.result;
  const allChecked = outcome ? accepted.size === outcome.entities.length : false;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Two ways to bring a document in. <strong>Index</strong> records what the document is
        (author, version, dates, hash) and which known things it mentions, in the catalog, with
        no model; the text is not stored. <strong>Extract</strong> asks the model to propose
        entities and their properties for you to accept; every statement written cites the
        document. PDF, Word (.docx), HTML, text and Markdown; an older Word file (.doc) must be saved as .docx or PDF first.
      </p>

      {entitled.data !== undefined && !inPlan && (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
          Document extraction is not included in this site&apos;s plan.
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="file"
          accept=".pdf,.docx,.html,.htm,.txt,.md"
          disabled={uploadsLocked || busy !== null}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-sm"
        />
        <button
          type="button"
          onClick={() => void index()}
          disabled={uploadsLocked || !file || busy !== null}
          className={btnGhost}
          title="Record the document and its mentions of known things, no model"
        >
          {busy === 'index' ? 'Indexing…' : 'Index'}
        </button>
        <button
          type="button"
          onClick={() => void start()}
          disabled={!inPlan || uploadsLocked || !file || busy !== null}
          className={btnPrimary}
          title={inPlan ? 'Ask the model to propose facts from the document' : "Not included in this site's plan"}
        >
          {busy === 'start' ? 'Starting…' : 'Extract with the model'}
        </button>
        {uploadsLocked && (
          <span className="text-xs text-muted-foreground">Uploads are paused while a load runs.</span>
        )}
      </div>

      {indexed && (
        <div className={messageClass('outcome')}>
          <div className="font-medium">Indexed {indexed.document.filename}</div>
          <p className="mt-1">
            Recorded as <span className="font-mono">{indexed.document.iri}</span> in the catalog
            {indexed.facts.author ? <>, author <strong>{indexed.facts.author}</strong></> : null}
            {indexed.facts.version ? <>, version <strong>{indexed.facts.version}</strong></> : null}
            {indexed.facts.date ? <>, dated {indexed.facts.date}</> : null}
            {indexed.facts.modified_at ? <>, last modified {indexed.facts.modified_at.slice(0, 10)}</> : null}
            . {indexed.mentions.length} known thing{indexed.mentions.length === 1 ? '' : 's'} mentioned
            across {indexed.sections} section{indexed.sections === 1 ? '' : 's'} (matched against{' '}
            {indexed.lexicon_terms.toLocaleString()} labels);{' '}
            {(indexed.statements ?? indexed.statements_planned).toLocaleString()} statements written.
            {indexed.facts.stated_as_placeholder.length > 0 && (
              <>
                {' '}
                Stated but not recorded: {indexed.facts.stated_as_placeholder.join(', ')} — a
                placeholder is not a value, and never an identity.
              </>
            )}
          </p>
          {indexed.mentions.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs">
              {indexed.mentions.map((m) => (
                <li key={m.iri}>
                  <span className="font-medium">{m.label}</span>{' '}
                  <span className="font-mono text-muted-foreground">{m.iri}</span> · {m.count}×
                  {m.sections.length > 0 ? ` in ${m.sections.join('; ')}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {job && job.state === 'running' && (
        <div className="rounded-md border border-border p-3 text-sm">
          <div className="font-medium">
            Extracting {job.filename} ({job.format}) — {job.progress.phase}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            This runs on the server; you can leave this tab and come back. Recent extractions are
            listed below.
          </p>
        </div>
      )}

      {job && job.state === 'failed' && !error && (
        <Message kind="warning">
          The extraction did not finish. {describeApiError(job.http_status ?? 500, job.error, job.detail)}
        </Message>
      )}

      {outcome && !result?.ok && (
        <div className="space-y-3">
          <div className="text-sm">
            <span className="font-medium">{outcome.entities.length}</span> entit
            {outcome.entities.length === 1 ? 'y' : 'ies'} proposed from{' '}
            <span className="font-mono">{outcome.document.filename}</span> ({outcome.total_chunks}{' '}
            chunk{outcome.total_chunks === 1 ? '' : 's'}, {outcome.total_tokens.toLocaleString()}{' '}
            tokens, model {outcome.model}). Untick what is wrong.
          </div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs">
                <tr>
                  <th className="px-3 py-1">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      aria-label="Accept all"
                      onChange={(e) =>
                        setAccepted(
                          e.target.checked ? new Set(outcome.entities.map((x) => x.id)) : new Set(),
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1">Class</th>
                  <th className="px-3 py-1">Label</th>
                  <th className="px-3 py-1">Confidence</th>
                  <th className="px-3 py-1">Properties</th>
                  <th className="px-3 py-1">Matches existing</th>
                </tr>
              </thead>
              <tbody>
                {outcome.entities.map((e) => {
                  const on = accepted.has(e.id);
                  const open = expanded.has(e.id);
                  return (
                    <tr key={e.id} className={cn('border-t border-border', !on && 'text-muted-foreground')}>
                      <td className="px-3 py-1">
                        <input
                          type="checkbox"
                          checked={on}
                          aria-label={`Accept ${e.label}`}
                          onChange={(ev) =>
                            setAccepted((prev) => {
                              const next = new Set(prev);
                              if (ev.target.checked) next.add(e.id);
                              else next.delete(e.id);
                              return next;
                            })
                          }
                        />
                      </td>
                      <td className="px-3 py-1 font-mono">{e.class}</td>
                      <td className="px-3 py-1">{e.label}</td>
                      <td className="px-3 py-1 tabular-nums">{e.confidence.toFixed(2)}</td>
                      <td className="px-3 py-1">
                        <button
                          type="button"
                          className="underline"
                          onClick={() =>
                            setExpanded((prev) => {
                              const next = new Set(prev);
                              if (next.has(e.id)) next.delete(e.id);
                              else next.add(e.id);
                              return next;
                            })
                          }
                        >
                          {e.properties.length} {open ? '▾' : '▸'}
                        </button>
                        {open && e.properties.length > 0 && (
                          <ul className="mt-1 text-xs">
                            {e.properties.map((p, i) => (
                              <li key={`${p.predicate}-${i}`}>
                                <span className="font-mono">{p.predicate}</span> = {p.value}{' '}
                                <span className="text-muted-foreground">({p.confidence.toFixed(2)})</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-3 py-1 font-mono text-xs">{e.existing_match ?? ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <span className="font-medium">Target graph</span>
              <select value={targetGraph} onChange={(e) => setTargetGraph(e.target.value)} className={selectCls}>
                {!graphs.some((g) => g.bare === targetGraph) && <option value={targetGraph}>{targetGraph}</option>}
                {graphs.map((g) => (
                  <option key={g.bare} value={g.bare}>
                    {g.bare}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1 text-sm">
              <input type="radio" name="ex-mode" checked={mode === 'append'} onChange={() => setMode('append')} />
              Append
            </label>
            <label className="flex items-center gap-1 text-sm">
              <input type="radio" name="ex-mode" checked={mode === 'replace'} onChange={() => setMode('replace')} />
              Replace the graph first
            </label>
            <button
              type="button"
              onClick={() => void commit()}
              disabled={busy !== null || uploadsLocked || accepted.size === 0}
              className={btnPrimary}
            >
              {busy === 'commit' ? 'Writing…' : `Write ${accepted.size} accepted`}
            </button>
          </div>
        </div>
      )}

      {result?.ok && (
        <div className={messageClass('outcome')}>
          <div className="font-medium">Written</div>
          <p className="mt-1">
            {result.data.entities_written} entit{result.data.entities_written === 1 ? 'y' : 'ies'},{' '}
            {(result.data.statements ?? result.data.statements_planned).toLocaleString()} statement
            {(result.data.statements ?? result.data.statements_planned) === 1 ? '' : 's'} into{' '}
            <span className="font-mono">{result.data.graph}</span>, each citing{' '}
            <span className="font-mono">{result.data.document.iri}</span> ({result.data.document.filename}) in
            the catalog.
          </p>
          <button type="button" className={cn(btnGhost, 'mt-2')} onClick={() => { setJob(null); setResult(null); setFile(null); }}>
            Extract another
          </button>
        </div>
      )}

      {recent.length > 0 && (
        <div className="text-sm">
          <div className="font-medium">Recent extractions</div>
          <ul className="mt-1 space-y-1">
            {recent.map((j) => (
              <li key={j.job_id} className="flex flex-wrap items-center gap-2">
                <button type="button" className="underline" onClick={() => openJob(j)} disabled={busy !== null}>
                  {j.filename}
                </button>
                <span className="text-xs text-muted-foreground">
                  {j.state}
                  {j.result ? ` · ${j.result.entities.length} entities` : ''} · {j.started_at.slice(0, 16).replace('T', ' ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
